import type { ClientMessage, IFrameMessage } from './messages';

/**
 * Reasons why registration can fail
 */
export enum FailureReason {
  StorageAccessError,
  UnknownError,
}

// Register a cross-origin service worker inside an iframe. The provided
// registration page must be a document running on the appropriate origin which
// will call registerServiceWorkerAndNotifyOuterDocument(); that function
// implements the other half of the protocol. Returns a promise that resolves
// when the service worker is registered and ready. See `iframe.ts` for the
// other half of the protocol.
export function registerCrossOriginServiceWorker(
  registrationPageHref: string,
  parentDoc: Document = document,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const iframe: HTMLIFrameElement = parentDoc.createElement('iframe');

    const channel = new MessageChannel();
    channel.port1.onmessage = (message: MessageEvent<IFrameMessage>) => {
      if (message.data === 'ready') {
        console.debug(`Received message that the cross-origin service worker is ready.`);
        parentDoc.body.removeChild(iframe);
        resolve();
        return;
      }

      if (message.data === 'storageAccessError') {
        reject({
          message: 'Storage access is required',
          reason: FailureReason.StorageAccessError,
        });
      }

      if (message.data === 'unknownError') {
        reject({
          message: 'An unknown error occurred',
          reason: FailureReason.UnknownError,
        });
      }

      reject({
        message: `Received unexpected message: ${message.data}`,
        reason: FailureReason.UnknownError,
      });
    };

    iframe.setAttribute('style', 'display: none');
    iframe.src = registrationPageHref;
    iframe.addEventListener('load', () => {
      console.debug(`Got load event for registration page. Sending MessagePort`);
      // NOTE: this typed const helps ensure message type safety
      const message: ClientMessage = 'init';
      iframe.contentWindow!.postMessage(message, '*', [channel.port2]);
    });
    console.debug(`Attached iframe that will register service worker`);
    parentDoc.body.appendChild(iframe);
  });
}
